import React, { lazy, ReactElement } from 'react';
import { useTypedSelector } from '../useTypedSelector';
import selectors from 'src/redux/selectors';
import { PAGES_ENUM } from 'src/routes';
import SettingsPayPage from 'src/pages/SettingsPayPage/SettingsPayPage';
import SignInPage from 'src/pages/SignInPage/SignInPage';
import cls from 'src/components/layout/Sidebar/Sidebar.module.scss';
import { useTranslation } from 'react-i18next';
import { HouseIcon, StorageIcon } from 'src/assets/icons/buildings';
import {
  DocsIcon,
  DotListIcon,
  LongListIcon,
  MenuIcon,
  PictureListIcon,
  TabletIcon,
} from 'src/assets/icons/lists';
import DoubleCheckIcon from 'src/assets/icons/check/DoubleCheckIcon';
import {
  BoxIcon,
  CartIcon,
  CircleCloseIcon,
  DeleteIcon,
  EarthIcon,
  KeyIcon,
  MegaphoneIcon,
  PuzzleIcon,
} from 'src/assets/icons/tools';
import {
  CustomerAgentIcon,
  TeamIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
} from 'src/assets/icons/scins';
import {
  AdjustmentIcon,
  AppIcon,
  AppPlusIcon,
  HeadBranchIcon,
  SubtaskIcon,
  WorldPinIcon,
} from 'src/assets/icons/web';
import UpDownArrowsIcon from 'src/assets/icons/arrows/UpDownArrowsIcon';
import DeliveryTruckIcon from 'src/assets/icons/trucks/DeliveryTruckIcon';
import { ChatIcon, OpenEnvelopeIcon } from 'src/assets/icons/massages';
import MassengerIcon from 'src/assets/icons/massages/MassengerIcon';
import ChartLineIcon from 'src/assets/icons/charts/ChartLineIcon';
import { CoinsHandIcon, HeartHandshakeIcon } from 'src/assets/icons/hands';
import DollarIcon from 'src/assets/icons/money/DollarIcon';
import ClockIcon from 'src/assets/icons/clocks/ClockIcon';

export interface RouteItem {
  title: string;
  path: string;
  disabled: boolean;
  icon?: ReactElement;
}
export interface NavigationItem {
  icon: ReactElement;
  path?: string;
  title: string;
  id?: string;
  className?: string;
  routes?: RouteItem[];
  disabled: boolean;
}
const StatisticsPage = lazy(
  () => import('src/pages/StatisticsPage/StatisticsPage')
);
const FaqPage = lazy(() => import('src/pages/FaqPage/FaqPage'));
const DocumentationPage = lazy(
  () => import('src/pages/DocumentanionPage/DocumentationPage')
);

const OrderStatusesPage = lazy(
  () => import('src/pages/OrderStatuses/OrderStatusesPage')
);
const CreateOrderPage = lazy(() => import('src/pages/CreateOrderPage'));
const OrdersPage = lazy(() => import('src/pages/OrdersPage/OrdersPage'));
const CartPage = lazy(() => import('src/pages/CartPage/CartPage'));

const HomePage = lazy(() => import('src/pages/HomePage/HomePage'));

const ProductsProducersPage = lazy(
  () => import('src/pages/ProductsProducersPage/ProductsProducersPage')
);
const ProductCategoriesPage = lazy(
  () => import('src/pages/ProductCategoriesPage/ProductCategoriesPage')
);
const ProductsAttributesPage = lazy(
  () => import('src/pages/ProductsAttributesPage/ProductsAttributesPage')
);
const ProductsAttributeCategoriesPage = lazy(
  () =>
    import(
      'src/pages/ProductsAttributeCategoriesPage/ProductsAttributeCategoriesPage'
    )
);
const ProductsPage = lazy(() => import('src/pages/ProductsPage/ProductsPage'));

const PartnersPage = lazy(() => import('src/pages/PartnersPage/PartnersPage'));
const DeliveryServicePage = lazy(
  () => import('src/pages/DeliveryServicePage/DeliveryServicePage')
);
const RegistersPage = lazy(
  () => import('src/pages/RegistersPage/RegistersPage')
);
const SmsTemplatesPage = lazy(
  () => import('src/pages/SmsTemplatesPage/SmsTemplatesPage')
);
const SmsMailingPage = lazy(
  () => import('src/pages/SmsMailingPage/SmsMailingPage')
);
const ExpiredSubscriptionPage = lazy(
  () => import('src/pages/ExpiredSubscriptionPage/ExpiredSubscriptionPage')
);
const InvoicesPage = lazy(() => import('src/pages/InvoicesPage/InvoicesPage'));

const ClientsPage = lazy(() => import('src/pages/ClientsPage/ClientsPage'));
const ClientGroupsPage = lazy(
  () => import('src/pages/ClientGroupsPage/ClientGroupsPage')
);

const WarehouseArrivalPage = lazy(
  () => import('src/pages/WarehouseArrivalPage/WarehouseArrivalPage')
);
const WarehouseSuppliersPage = lazy(
  () => import('src/pages/WarehouseSuppliersPage/WarehouseSuppliersPage')
);
const WarehouseWriteoff = lazy(
  () => import('src/pages/WarehouseWriteoff/WarehouseWriteoff')
);
const ProductMovement = lazy(
  () => import('src/pages/ProductMovement/ProductMovement')
);

const SettingsHistoryPage = lazy(
  () => import('src/pages/SettingsHistoryPage/SettingsHistoryPage')
);
const SettingsSystemPage = lazy(
  () => import('src/pages/SettingsSystemPage/SettingsSystemPage')
);
const SettingsDepartmentsPage = lazy(
  () => import('src/pages/SettingsDepartmentsPage/SettingsDepartmentsPage')
);
const SettingsUserGroupsPage = lazy(
  () => import('src/pages/SettingsUserGroupsPage/SettingsUserGroupsPage')
);

const CreateUserGroupPage = lazy(
  () => import('src/pages/CreateUserGroupPage/CreateUserGroupPage')
);
const CreateUserPage = lazy(
  () => import('src/pages/CreateUserPage/CreateUserPage')
);
const SettingsUsersPage = lazy(
  () => import('src/pages/SettingsUsersPage/SettingsUsersPage')
);
const SettingsCountriesPage = lazy(
  () => import('src/pages/SettingsCountriesPage/SettingsCountriesPage')
);
const SettingsCurrenciesPage = lazy(
  () => import('src/pages/SettingsCurrenciesPage/SettingsCurrenciesPage')
);
// const SettingsPayPage = lazy(
//   () => import('src/pages/SettingsPayPage/SettingsPayPage')
// );
const SettingsBlockedIpPage = lazy(
  () => import('src/pages/SettingsBlockedIpPage/SettingsBlockedIpPage')
);
const SettingsModulesPage = lazy(
  () => import('src/pages/SettingsModulesPage/SettingsModulesPage')
);
// const SignInPage = lazy(() => import('src/pages/SignInPage/SignInPage'));
const PasswordReset = lazy(
  () => import('src/pages/PasswordReset/PasswordReset')
);
const UiPage = lazy(() => import('src/pages/UiPage/UiPage'));
const SubscriptionPage = lazy(
  () => import('src/pages/SubscriptionsPage/SubscriptionsPage')
);
const PaymentHistoryPage = lazy(
  () => import('src/pages/PaymentHistoryPage/PaymentHistoryPage')
);

const UserPermissionHistoryPage = lazy(
  () => import('src/pages/UserPermissionHistoryPage/UserPermissionHistoryPage')
);
const ForbiddenPage = lazy(
  () => import('src/pages/ForbiddenPage/ForbiddenPage')
);

const useNavigations = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'sidebar' });
  const user = useTypedSelector(selectors.getUser);
  const permissions = useTypedSelector(selectors.getPermissions);

  const navigations: NavigationItem[] = [
    {
      id: 'home',
      icon: <HouseIcon />,
      path: PAGES_ENUM.HOME,
      title: t('desktop'),
      disabled: false,
    },
    {
      id: 'orders',
      icon: <LongListIcon />,
      title: t('orders'),
      disabled: !permissions.can_order_module,
      routes: [
        {
          icon: <DoubleCheckIcon />,
          title: t('statuses'),
          path: PAGES_ENUM.ORDER_STATUSES,
          disabled: !permissions.can_use_order_status,
        },
        {
          icon: <DotListIcon />,
          title: t('list'),
          path: PAGES_ENUM.ORDERS,
          disabled: !permissions.can_use_order_list,
        },
        {
          icon: <DeleteIcon />,
          title: t('cart'),
          path: PAGES_ENUM.CART,
          disabled: !permissions.can_use_order_cart,
        },
      ],
    },
    {
      id: 'clients',
      icon: <UserIcon />,
      title: t('clients'),
      disabled: !permissions.can_client_module,
      routes: [
        {
          icon: <UsersIcon />,
          title: t('groups'),
          path: PAGES_ENUM.CLIENT_GROUPS,
          disabled: !permissions.can_use_client_group,
        },
        {
          icon: <DotListIcon />,
          title: t('list'),
          path: PAGES_ENUM.CLIENTS,
          disabled: !permissions.can_use_client,
        },
      ],
    },
    {
      id: 'warehouse',
      icon: <StorageIcon />,
      title: t('warehouse'),
      disabled: !permissions.can_stock_module,
      routes: [
        {
          icon: <UserGroupIcon />,
          title: t('suppliers'),
          path: PAGES_ENUM.WAREHOUSE_SUPPLIERS,
          disabled: !permissions.can_use_product_provider,
        },
        {
          icon: <AppPlusIcon />,
          title: t('arrival'),
          path: PAGES_ENUM.WAREHOUSE_ARRIVAL,
          disabled: !permissions.can_use_consignment,
        },
        {
          icon: <AppIcon />,
          title: t('writeoff'),
          path: PAGES_ENUM.WAREHOUSE_WRITE_OFF,
          disabled: !permissions.can_use_write_off,
        },
        {
          icon: <UpDownArrowsIcon />,
          title: t('movement'),
          path: PAGES_ENUM.PRODUCT_MOVEMENT,
          disabled: !permissions.can_use_movement,
        },
      ],
    },
    {
      id: 'products',
      icon: <BoxIcon />,
      title: t('products'),
      disabled: !permissions.can_product_module,
      routes: [
        {
          icon: <WorldPinIcon />,
          title: t('producers'),
          path: PAGES_ENUM.PRODUCTS_PRODUCERS,
          disabled: !permissions.can_use_product_producer,
        },
        {
          icon: <DocsIcon />,
          title: t('categories'),
          path: PAGES_ENUM.PRODUCTS_CATEGORIES,
          disabled: !permissions.can_use_product_category,
        },
        {
          icon: <DotListIcon />,
          title: t('list'),
          path: PAGES_ENUM.PRODUCTS,
          disabled: !permissions.can_use_product,
        },
        {
          icon: <PictureListIcon />,
          title: t('attribute-categories'),
          path: PAGES_ENUM.PRODUCTS_ATTRIBUTE_CATEGORY,
          disabled: !permissions.can_use_product_attribute_category,
        },
        {
          icon: <MenuIcon />,
          title: t('attributes'),
          path: PAGES_ENUM.PRODUCTS_ATTRIBUTES,
          disabled: !permissions.can_use_product_attribute,
        },
      ],
    },
    {
      id: 'dispatch',
      icon: <CartIcon />,
      title: t('dispatch'),
      disabled: !permissions.can_moving_module,
      routes: [
        {
          icon: <DeliveryTruckIcon />,
          title: t('delivery-service'),
          path: PAGES_ENUM.DELIVERY_SERVICE,
          disabled: !permissions.can_use_delivery_type,
        },
        {
          icon: <TabletIcon />,
          title: t('invoices'),
          path: PAGES_ENUM.INVOICES,
          disabled: !permissions.can_use_invoices,
        },
        {
          icon: <SubtaskIcon />,
          title: t('registers'),
          path: PAGES_ENUM.REGISTERS,
          disabled: !permissions.can_use_registry,
        },
      ],
    },
    {
      id: 'newsletter',
      icon: <MegaphoneIcon />,
      title: t('newsletter'),
      disabled: !permissions.can_sms_module,
      routes: [
        {
          icon: <ChatIcon />,
          title: t('sms-templates'),
          path: PAGES_ENUM.SMS_TEMPLATES,
          disabled: !permissions.can_use_sms_template,
        },
        {
          icon: <MassengerIcon />,
          title: t('sms-mailing'),
          path: PAGES_ENUM.SMS_MAILING,
          disabled: !permissions.can_use_sms,
        },
      ],
    },
    {
      id: 'statistics',
      icon: <ChartLineIcon />,
      path: PAGES_ENUM.STATISTICS,
      title: t('statistics'),
      disabled: !permissions.can_use_statistics,
    },
    {
      id: 'settings',
      icon: <AdjustmentIcon />,
      title: t('settings'),
      disabled: !permissions.can_settings_module,
      routes: [
        {
          icon: <KeyIcon />,
          title: t('system'),
          path: PAGES_ENUM.SETTINGS_SYSTEM,
          disabled: !user.is_superuser,
        },
        {
          icon: <OpenEnvelopeIcon />,
          title: t('modules'),
          path: PAGES_ENUM.SETTINGS_MODULES,
          disabled: !permissions.can_use_modules,
        },
        {
          icon: <HeadBranchIcon />,
          title: t('departments'),
          path: PAGES_ENUM.SETTINGS_DEPARTMENTS,
          disabled: !permissions.can_use_office,
        },
        {
          icon: <TeamIcon />,
          title: t('users-groups'),
          path: PAGES_ENUM.SETTINGS_USER_GROUPS,
          disabled: !permissions.can_use_roles,
        },
        {
          icon: <UserPlusIcon />,
          title: t('users'),
          path: PAGES_ENUM.SETTINGS_USERS,
          disabled: !permissions.can_update_user,
        },
        {
          icon: <EarthIcon />,
          title: t('countries'),
          path: PAGES_ENUM.SETTINGS_COUNTRIES,
          disabled: !permissions.can_use_country,
        },
        {
          icon: <CoinsHandIcon />,
          title: t('pay'),
          path: PAGES_ENUM.SETTINGS_PAY,
          disabled: !permissions.can_use_payment_type,
        },
        {
          icon: <DollarIcon />,
          title: t('currencies'),
          path: PAGES_ENUM.SETTINGS_CURRENCIES,
          disabled: !permissions.can_use_currency,
        },
        {
          icon: <CircleCloseIcon />,
          title: t('blocked-ip'),
          path: PAGES_ENUM.SETTINGS_BLOCKED_IP,
          disabled: !permissions.can_use_ip,
        },
        {
          icon: <ClockIcon />,
          title: t('history'),
          path: PAGES_ENUM.SETTINGS_HISTORY,
          disabled: !permissions.can_use_history,
        },
        {
          icon: <PuzzleIcon />,
          title: t('documentation-api'),
          path: PAGES_ENUM.FAQ_DOCUMENTATION_API,
          disabled: !permissions.can_use_faq_api,
        },
      ],
    },
    {
      id: 'faq',
      icon: <CustomerAgentIcon className={cls.faq_icon} />,
      path: PAGES_ENUM.FAQ,
      title: 'FAQ',
      disabled: !permissions.can_use_faq,
    },
    {
      icon: <HeartHandshakeIcon />,
      path: PAGES_ENUM.PARTNERS,
      className: '',
      title: t('partners'),
      disabled: false,
    },
  ];

  const routes = [
    {
      path: PAGES_ENUM.HOME,
      component: <HomePage />,
      isAvailable: true,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.CLIENTS,
      component: <ClientsPage />,
      isAvailable: permissions?.can_use_client,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.CLIENT_GROUPS,
      component: <ClientGroupsPage />,
      isAvailable: permissions?.can_use_client_group,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.ORDERS,
      component: <OrdersPage />,
      isAvailable: permissions?.can_use_order_list,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.CREATE_ORDER,
      component: <CreateOrderPage />,
      isAvailable: permissions?.can_create_order,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.COPY_ORDER,
      component: <CreateOrderPage />,
      isAvailable: permissions?.can_use_order_list,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.UPDATE_ORDER,
      component: <CreateOrderPage />,
      isAvailable: permissions?.can_edit_order,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.ORDER_STATUSES,
      component: <OrderStatusesPage />,
      isAvailable: permissions?.can_use_order_status,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.CART,
      component: <CartPage />,
      isAvailable: permissions?.can_use_order_cart,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.WAREHOUSE_SUPPLIERS,
      component: <WarehouseSuppliersPage />,
      isAvailable: permissions?.can_use_product_provider,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.WAREHOUSE_ARRIVAL,
      component: <WarehouseArrivalPage />,
      isAvailable: permissions?.can_use_consignment,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.WAREHOUSE_WRITE_OFF,
      component: <WarehouseWriteoff />,
      isAvailable: permissions?.can_use_write_off,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.PRODUCT_MOVEMENT,
      component: <ProductMovement />,
      isAvailable: permissions?.can_use_movement,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.PARTNERS,
      component: <PartnersPage />,
      isAvailable: true,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.PRODUCTS_PRODUCERS,
      component: <ProductsProducersPage />,
      isAvailable: permissions?.can_use_product_producer,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.PRODUCTS_CATEGORIES,
      component: <ProductCategoriesPage />,
      isAvailable: permissions?.can_use_product_category,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.PRODUCTS_ATTRIBUTES,
      component: <ProductsAttributesPage />,
      isAvailable: permissions?.can_use_product_attribute,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.PRODUCTS_ATTRIBUTE_CATEGORY,
      component: <ProductsAttributeCategoriesPage />,
      isAvailable: permissions?.can_use_product_attribute_category,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.PRODUCTS,
      component: <ProductsPage />,
      isAvailable: permissions?.can_use_product,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.SETTINGS_SYSTEM,
      component: <SettingsSystemPage />,
      isAvailable: user.is_superuser,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_DEPARTMENTS,
      component: <SettingsDepartmentsPage />,
      isAvailable: permissions?.can_use_office,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_USER_GROUPS,
      component: <SettingsUserGroupsPage />,
      isAvailable: permissions?.can_use_roles,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.CREATE_USER_GROUP,
      component: <CreateUserGroupPage />,
      isAvailable: permissions?.can_use_roles,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.UPDATE_USER_GROUP,
      component: <CreateUserGroupPage />,
      isAvailable: permissions?.can_use_roles,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_USERS,
      component: <SettingsUsersPage />,
      isAvailable: permissions?.can_update_user,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.CREATE_USER,
      component: <CreateUserPage />,
      isAvailable: permissions?.can_update_user,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.UPDATE_USER,
      component: <CreateUserPage />,
      isAvailable: permissions?.can_update_user,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_COUNTRIES,
      component: <SettingsCountriesPage />,
      isAvailable: permissions?.can_use_country,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_CURRENCIES,
      component: <SettingsCurrenciesPage />,
      isAvailable: permissions?.can_use_currency,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_PAY,
      component: <SettingsPayPage />,
      isAvailable: permissions?.can_use_payment_type,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_BLOCKED_IP,
      component: <SettingsBlockedIpPage />,
      isAvailable: permissions?.can_use_ip,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_MODULES,
      component: <SettingsModulesPage />,
      isAvailable: permissions?.can_use_modules,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SETTINGS_HISTORY,
      component: <SettingsHistoryPage />,
      isAvailable: permissions?.can_use_history,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.DELIVERY_SERVICE,
      component: <DeliveryServicePage />,
      isAvailable: permissions?.can_use_delivery_type,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.REGISTERS,
      component: <RegistersPage />,
      isAvailable: permissions?.can_use_registry,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.INVOICES,
      component: <InvoicesPage />,
      isAvailable: permissions?.can_use_invoices,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.SMS_TEMPLATES,
      component: <SmsTemplatesPage />,
      isAvailable: permissions?.can_use_sms_template,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SMS_MAILING,
      component: <SmsMailingPage />,
      isAvailable: permissions?.can_use_sms,
      disableLayout: false,
    },

    {
      path: PAGES_ENUM.STATISTICS,
      component: <StatisticsPage />,
      isAvailable: permissions?.can_use_statistics,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.FAQ,
      component: <FaqPage />,
      isAvailable: permissions?.can_use_faq,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.EXPIRED_SUBSCRIPTIONS,
      component: <ExpiredSubscriptionPage />,
      isAvailable: true,
      disableLayout: true,
    },
    {
      path: PAGES_ENUM.SIGN_IN,
      component: <SignInPage />,
      isAvailable: true,
      disableLayout: true,
    },
    {
      path: PAGES_ENUM.RESET_PASSWORD,
      component: <PasswordReset />,
      isAvailable: true,
      disableLayout: true,
    },
    {
      path: PAGES_ENUM.UPDATE_PASSWORD,
      component: <PasswordReset isUpdate />,
      isAvailable: true,
      disableLayout: true,
    },
    {
      path: PAGES_ENUM.FAQ_DOCUMENTATION_API,
      component: <DocumentationPage />,
      isAvailable: permissions?.can_use_faq_api,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.UI,
      component: <UiPage />,
      isAvailable: true,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.SUBSCRIPTIONS,
      component: <SubscriptionPage />,
      isAvailable: true,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.PAYMENTS_HISTORY,
      component: <PaymentHistoryPage />,
      isAvailable: true,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.USER_PERMISSION_HISTORY,
      component: <UserPermissionHistoryPage />,
      isAvailable: permissions?.can_update_user,
      disableLayout: false,
    },
    {
      path: PAGES_ENUM.FORBIDDEN,
      component: <ForbiddenPage />,
      isAvailable: true,
      disableLayout: true,
    },
  ];

  return { navigations, routes };
};

export default useNavigations;
